import * as yup from 'yup';

import { RowActionsDropdown } from '../../component/common/table/action-dropdown/RowActionsDropdown';

import { formatCurrency } from '../../utils/currency/tools';
import { ITEM_TAG_LABELS, ITEM_TAG_OPTIONS } from '../../utils/itemTags';

/* ── Custom Field (category) ─────────────────────────────────────────────── */

export const getCustomFieldColumnDefinition = (onEdit, onView, onDelete) => [
  {
    id: 'imageUrl',
    accessorKey: 'imageUrl',
    size: 56,
    minSize: 56,
    maxSize: 56,
    header: '',
    cell: ({ row }) =>
      row.original.imageUrl ? (
        <img
          src={row.original.imageUrl}
          alt=""
          style={{ width: '32px', height: '32px', margin: '6px', borderRadius: '4px' }}
        />
      ) : (
        <div
          style={{
            width: '32px',
            height: '32px',
            margin: '6px',
            borderRadius: '4px',
            background: 'var(--background-variant)'
          }}
        />
      )
  },
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Category',
    size: 200,
    minSize: 200,
    filterFn: 'tableFilterFn'
  },
  {
    id: 'active',
    accessorKey: 'active',
    header: 'Active',
    size: 80,
    minSize: 80,
    cell: ({ row }) => (row.original.active ? 'Yes' : 'No')
  },
  {
    id: 'actions',
    header: '',
    size: 40,
    minSize: 40,
    maxSize: 40,
    enableSorting: false,
    disableExport: true,
    cell: ({ row }) => {
      const actions = [
        onView && { label: 'View', value: 'view', onSelect: onView },
        onEdit && { label: 'Edit', value: 'edit', onSelect: onEdit },
        onDelete && { label: 'Delete', value: 'delete', onSelect: onDelete }
      ].filter(Boolean);
      return <RowActionsDropdown row={row} actions={actions} />;
    }
  }
];

export const CUSTOM_FIELD_TABS = () => [
  {
    label: 'Details',
    fields: [
      {
        type: 'input',
        name: 'name',
        label: 'Name',
        required: true,
        validation: yup.string().required('Name is required')
      },
      {
        type: 'yesno',
        name: 'active',
        label: 'Active',
        defaultValue: true,
        validation: yup.boolean().optional()
      },
      {
        type: 'image',
        name: 'imageUrl',
        label: 'Image',
        colSpan: 2,
        validation: yup.string().nullable().optional()
      }
    ]
  }
];

/* ── Custom Field Item ────────────────────────────────────────────────────── */

export const getCustomFieldItemColumnDefinition = (onEdit, onView, onDelete) => [
  {
    id: 'imageUrl',
    accessorKey: 'imageUrl',
    size: 56,
    minSize: 56,
    maxSize: 56,
    header: '',
    cell: ({ row }) =>
      row.original.imageUrl ? (
        <img
          src={row.original.imageUrl}
          alt=""
          style={{ width: '32px', height: '32px', margin: '6px', borderRadius: '4px' }}
        />
      ) : (
        <div
          style={{
            width: '32px',
            height: '32px',
            margin: '6px',
            borderRadius: '4px',
            background: 'var(--background-variant)'
          }}
        />
      )
  },
  {
    id: 'fieldName',
    accessorKey: 'fieldName',
    header: 'Category',
    size: 140,
    minSize: 140,
    filterFn: 'tableFilterFn'
  },
  {
    id: 'name',
    accessorKey: 'name',
    header: 'Item',
    size: 180,
    minSize: 180,
    filterFn: 'tableFilterFn'
  },
  {
    id: 'price',
    accessorKey: 'price',
    header: 'Price / g',
    size: 100,
    minSize: 100,
    cell: ({ row }) => (row.original.price != null ? formatCurrency(row.original.price) : '—')
  },
  {
    id: 'calories',
    accessorKey: 'calories',
    header: 'Kcal / 100g',
    size: 110,
    minSize: 110,
    cell: ({ row }) => (row.original.calories != null ? row.original.calories : '—')
  },
  {
    id: 'protein',
    accessorKey: 'protein',
    header: 'Protein',
    size: 90,
    minSize: 90,
    cell: ({ row }) => (row.original.protein != null ? `${row.original.protein}g` : '—')
  },
  {
    id: 'carbs',
    accessorKey: 'carbs',
    header: 'Carbs',
    size: 90,
    minSize: 90,
    cell: ({ row }) => (row.original.carbs != null ? `${row.original.carbs}g` : '—')
  },
  {
    id: 'fat',
    accessorKey: 'fat',
    header: 'Fat',
    size: 90,
    minSize: 90,
    cell: ({ row }) => (row.original.fat != null ? `${row.original.fat}g` : '—')
  },
  {
    id: 'tags',
    accessorKey: 'tags',
    header: 'Tags',
    size: 160,
    minSize: 160,
    cell: ({ row }) =>
      row.original.tags?.length
        ? row.original.tags.map((t) => ITEM_TAG_LABELS[t] ?? t).join(', ')
        : '—'
  },
  {
    id: 'actions',
    header: '',
    size: 40,
    minSize: 40,
    maxSize: 40,
    enableSorting: false,
    disableExport: true,
    cell: ({ row }) => {
      const actions = [
        onView && { label: 'View', value: 'view', onSelect: onView },
        onEdit && { label: 'Edit', value: 'edit', onSelect: onEdit },
        onDelete && { label: 'Delete', value: 'delete', onSelect: onDelete }
      ].filter(Boolean);
      return <RowActionsDropdown row={row} actions={actions} />;
    }
  }
];

export const CUSTOM_FIELD_ITEM_TABS = (customFields) => [
  {
    label: 'Details',
    fields: [
      {
        type: 'select',
        name: 'fieldId',
        label: 'Category',
        required: true,
        options: (Array.isArray(customFields) ? customFields : []).map((f) => ({
          key: f.id,
          label: f.name,
          value: f.id,
          active: true
        })),
        validation: yup.string().required('Category is required')
      },
      {
        type: 'input',
        name: 'name',
        label: 'Name',
        required: true,
        validation: yup.string().required('Name is required')
      },
      {
        type: 'decimal',
        name: 'price',
        label: 'Price per gram (R)',
        validation: yup.number().nullable().optional()
      },
      {
        type: 'image',
        name: 'imageUrl',
        label: 'Icon (optional, falls back to the category icon)',
        colSpan: 2,
        compact: true,
        validation: yup.string().nullable().optional()
      },
      {
        type: 'label',
        name: 'macros_label',
        label: 'Macros (per 100g)',
        colSpan: 2
      },
      {
        type: 'decimal',
        name: 'calories',
        label: 'Calories (kcal)',
        validation: yup.number().nullable().optional()
      },
      {
        type: 'decimal',
        name: 'protein',
        label: 'Protein (g)',
        validation: yup.number().nullable().optional()
      },
      {
        type: 'decimal',
        name: 'carbs',
        label: 'Carbs (g)',
        validation: yup.number().nullable().optional()
      },
      {
        type: 'decimal',
        name: 'fat',
        label: 'Fat (g)',
        validation: yup.number().nullable().optional()
      },
      {
        type: 'select',
        name: 'tags',
        label: 'Dietary Tags',
        colSpan: 2,
        multiSelect: true,
        defaultValue: [],
        options: ITEM_TAG_OPTIONS,
        validation: yup.array().of(yup.string()).optional()
      }
    ]
  }
];
